import { Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useEntityTemplateSteps } from "./EntityTemplateSteps.hooks";
import {
    DotLegalButton,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalPaper,
    DotLegalTooltip,
    useDotLegalTableStyles,
    useIsOnMediumScreen,
    useIsOnSmallScreen,
} from "@dotlegal/dotlegal-ui-components";
import CreateEditEntityTemplateStepDialog from "./createEditEntityTemplateStepDialog/CreateEditEntityTemplateStepDialog.tsx";
import React from "react";
import { useTranslation } from "../../localization/useTranslation.ts";
import { EntityTemplateStep } from "../EntityTemplate.types.ts";
import DeleteEntityTemplateStepDialog from "./deleteEntityTemplateStepDialog/DeleteEntityTemplateStepDialog.tsx";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckIcon from "@mui/icons-material/Check";

export interface EntityTemplateStepsProps {}

function EntityTemplateSteps() {
    const tableStyles = useDotLegalTableStyles(undefined, false, undefined);
    const isMediumScreen = useIsOnMediumScreen();
    const isSmallScreen = useIsOnSmallScreen();

    const { translateString } = useTranslation();
    const {
        id,
        isLoading,
        isStandardTemplate,
        steps,
        handleDragEnd,
        breadCrumbs,
        stepToDelete,
        setStepToDelete,
        onStepDeleted,
        editStep,
        stepToEdit,
        onCloseCreateEditStepDialog,
        showAddStepDialog,
        onStepSaved,
        setShowAddStepDialog,
    } = useEntityTemplateSteps();

    const getMenuItems = (step: EntityTemplateStep) => {
        if (step.fixed || isStandardTemplate) {
            return [];
        }

        return [
            {
                menuItem: translateString("edit"),
                onClick: () => editStep(step),
            },
            {
                menuItem: translateString("delete"),
                onClick: () => setStepToDelete(step!),
            },
        ];
    };

    function getSkeletonRows() {
        const rows = [1, 2, 3, 4, 5];
        return rows.map((r) => (
            <TableRow key={r}>
                {[0, 1, 2].map((h, i) => (
                    <TableCell key={i}>
                        <Skeleton />
                    </TableCell>
                ))}
            </TableRow>
        ));
    }

    const stepLimitReached = (steps?.length ?? 9) === 9;
    const width = isSmallScreen ? "150" : isMediumScreen ? "200" : "300";
    return (
        <Box>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    isStandardTemplate ? undefined : (
                        <DotLegalButton
                            buttonType={"primary"}
                            size="medium"
                            onClick={() => setShowAddStepDialog(true)}
                            disabled={stepLimitReached}
                            toolTip={stepLimitReached ? translateString("maxNumberOfEntityTemplateStepsAdded") : undefined}
                        >
                            {translateString("addStepToTemplate")}
                        </DotLegalButton>
                    )
                }
            />
            <DotLegalPaper>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <TableContainer sx={tableStyles.root}>
                        <Table sx={tableStyles.dpaTable}>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={width}>
                                        <TableSortLabel active disabled sx={tableStyles.headerText}>
                                            {translateString("stepOrder")}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell width={width}>
                                        <TableSortLabel disabled sx={tableStyles.headerText}>
                                            {translateString("step")}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell width={width}>
                                        <TableSortLabel disabled sx={tableStyles.headerText}>
                                            {translateString("required")}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell width={width}>
                                        <TableSortLabel disabled sx={tableStyles.headerText}></TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <Droppable droppableId="steps">
                                {(provided) => (
                                    <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                                        {isLoading
                                            ? getSkeletonRows()
                                            : steps?.map((step, index) => (
                                                  <Draggable key={step.id} draggableId={step.id} index={index} isDragDisabled={isStandardTemplate}>
                                                      {(provided, snapshot) => (
                                                          <DotLegalTooltip
                                                              text={
                                                                  isStandardTemplate
                                                                      ? undefined
                                                                      : step.fixed
                                                                        ? translateString("contractTemplateStepFixedTooltip")
                                                                        : undefined
                                                              }
                                                          >
                                                              <TableRow
                                                                  ref={provided.innerRef}
                                                                  {...provided.draggableProps}
                                                                  {...provided.dragHandleProps}
                                                                  sx={(theme) => ({
                                                                      "&:hover": {
                                                                          cursor: isStandardTemplate ? "inherit" : "grab !important",
                                                                      },
                                                                      ...(snapshot.isDragging && {
                                                                          background: theme.palette.primary.light,
                                                                          display: "table",
                                                                      }),
                                                                  })}
                                                              >
                                                                  <TableCell width={width}>
                                                                      <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", gap: 1 }}>
                                                                          {!isStandardTemplate && (
                                                                              <Box {...provided.dragHandleProps} sx={{ display: "flex" }}>
                                                                                  <DragIndicatorIcon
                                                                                      sx={(theme) => ({
                                                                                          fill: theme.palette.grey[500],
                                                                                          height: 20,
                                                                                          outline: "none",
                                                                                          "&:hover": {
                                                                                              cursor: "grab",
                                                                                          },
                                                                                      })}
                                                                                  />
                                                                              </Box>
                                                                          )}
                                                                          <Box>{step.order}</Box>
                                                                      </Box>
                                                                  </TableCell>

                                                                  <TableCell width={width}>{step.name}</TableCell>
                                                                  <TableCell width={width}>{step.fixed ? <CheckIcon /> : undefined}</TableCell>
                                                                  <TableCell align="right">
                                                                      {step.fixed ? undefined : <DotLegalOverflowMenu items={getMenuItems(step)} />}
                                                                  </TableCell>
                                                              </TableRow>
                                                          </DotLegalTooltip>
                                                      )}
                                                  </Draggable>
                                              ))}

                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </Table>
                    </TableContainer>
                </DragDropContext>
            </DotLegalPaper>

            {showAddStepDialog && (
                <CreateEditEntityTemplateStepDialog entityTemplateId={id!} onCloseDialog={onCloseCreateEditStepDialog} onStepSaved={onStepSaved} />
            )}

            {stepToEdit && (
                <CreateEditEntityTemplateStepDialog
                    entityTemplateId={id!}
                    onCloseDialog={onCloseCreateEditStepDialog}
                    onStepSaved={onStepSaved}
                    step={stepToEdit}
                />
            )}

            {stepToDelete && (
                <DeleteEntityTemplateStepDialog
                    entityTemplateId={id!}
                    stepId={stepToDelete.id}
                    stepName={stepToDelete.name}
                    onCloseDialog={() => setStepToDelete(undefined)}
                    onStepDeleted={onStepDeleted}
                />
            )}
        </Box>
    );
}

export default EntityTemplateSteps;
